import { useEffect } from 'react';
import {
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { getBackofficeAuthSettings, getBackofficeLoginRequest } from './config';
import { msalInstance } from './msal';
import { setBackofficeAccessTokenProvider } from '@/services/backoffice-api';

function resolveAccount(
  instance: IPublicClientApplication,
  accounts: AccountInfo[]
): AccountInfo | null {
  return instance.getActiveAccount() ?? accounts[0] ?? null;
}

function BackofficeAuthBridge({ children }: { children: React.ReactNode }) {
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const account = resolveAccount(instance, accounts);
    if (account) {
      instance.setActiveAccount(account);
    }
  }, [accounts, instance]);

  useEffect(() => {
    setBackofficeAccessTokenProvider(async () => {
      const account = resolveAccount(instance, accounts);
      if (!account) {
        throw new Error('No active backoffice account.');
      }

      try {
        const result = await instance.acquireTokenSilent({
          account,
          scopes: [getBackofficeAuthSettings().apiScope],
        });
        return result.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect(getBackofficeLoginRequest());
        }
        throw error;
      }
    });

    return () => setBackofficeAccessTokenProvider(null);
  }, [accounts, instance]);

  return <>{children}</>;
}

export function BackofficeAuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <BackofficeAuthBridge>{children}</BackofficeAuthBridge>
    </MsalProvider>
  );
}
